import { useState } from 'react'
import { BeadedChain } from '@/components/BeadedChain'
import { DebugPanel } from '@/components/DebugPanel'
import { useThemeToggle } from '@/hooks/useThemeToggle'
import { DEFAULT_CHAIN_CONFIG } from '@/types'
import type { ChainConfig } from '@/types'

function App() {
    const { mode, toggle } = useThemeToggle()
    const [chainConfig, setChainConfig] = useState<ChainConfig>(DEFAULT_CHAIN_CONFIG)
    const [chainKey, setChainKey] = useState(0)

    const handleConfigChange = (newConfig: ChainConfig) => {
        const needsRemount =
            newConfig.beadCount !== chainConfig.beadCount ||
            newConfig.segmentLength !== chainConfig.segmentLength
        setChainConfig(newConfig)
        if (needsRemount) {
            setChainKey((k) => k + 1)
        }
    }

    // Canvas width = 2 * chainLength + 2 * handleRadius; anchor is at center.
    // Position so anchor sits 42px from right viewport edge.
    const chainLength = (chainConfig.beadCount - 1) * chainConfig.segmentLength + chainConfig.handleRadius
    const canvasWidth = chainLength * 2 + chainConfig.handleRadius * 2
    const rightOffset = -(canvasWidth / 2 - 42)

    return (
        <div className="min-h-screen bg-background transition-colors duration-700 ease-in-out relative overflow-hidden">
            <div className="absolute top-0" style={{ right: rightOffset }}>
                <BeadedChain
                    key={chainKey}
                    onToggle={toggle}
                    theme={mode}
                    config={chainConfig}
                />
            </div>

            <div className="flex flex-col items-center justify-center min-h-screen gap-8 px-4 py-16">
                <h1 className="text-4xl font-bold text-foreground transition-colors duration-700">
                    Pull the chain
                </h1>
                <p className="text-muted-foreground transition-colors duration-700 text-lg">
                    {mode === 'light' ? 'Turn off the lights' : 'Turn on the lights'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 max-w-3xl w-full">
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="rounded-lg border bg-card text-card-foreground p-6 shadow-sm transition-colors duration-700"
                        >
                            <h3 className="font-semibold mb-2">Card {i}</h3>
                            <p className="text-sm text-muted-foreground">
                                Sample content to showcase the theme transition.
                            </p>
                        </div>
                    ))}
                </div>

                <p className="text-xs text-muted-foreground/50 mt-12">
                    Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-xs">D</kbd> to open debug panel
                </p>
            </div>

            <DebugPanel config={chainConfig} onConfigChange={handleConfigChange} />
        </div>
    )
}

export default App
